import { useState, useRef } from "react";
import Avatar from "@mui/material/Avatar";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { styled } from "../../../../lib/styled";

export interface AvatarUploadProps {
  /** Current image URL. Null/undefined shows initials or the default icon. */
  src?: string | null;
  /** Used to derive initials when no image is available. */
  name?: string | null;
  /** Avatar diameter in pixels. Defaults to 80. */
  size?: number;
  /** Called with the selected file; should upload and return the new public URL. */
  onUpload: (file: File) => Promise<string | null>;
  /** MIME types the file picker accepts. Defaults to JPEG, PNG, WebP. */
  accept?: string;
  /** Label shown beneath the avatar. Defaults to "Change photo". */
  label?: string;
}

function toInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

const CircularButton = styled(ButtonBase)({
  borderRadius: "50%",
  position: "relative",
});

const SizedAvatar = styled(Avatar)<{ $size: number }>(({ $size }) => ({
  width: $size,
  height: $size,
  fontSize: $size * 0.35,
}));

const UploadOverlay = styled("div")({
  position: "absolute",
  inset: 0,
  borderRadius: "50%",
  backgroundColor: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const WhiteSpinner = styled(CircularProgress)({
  color: "#fff",
});

/**
 * Clickable avatar that handles file selection, upload, and immediate display.
 * Manages local URL state and cache-busting so the new image appears as soon as
 * the upload resolves, without waiting for an external data refresh.
 */
export function AvatarUpload({
  src,
  name,
  size = 80,
  onUpload,
  accept = "image/jpeg,image/png,image/webp",
  label = "Change photo",
}: AvatarUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localSrc, setLocalSrc] = useState<string | null>(null);
  const [cacheBust, setCacheBust] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const baseSrc = localSrc ?? src ?? null;
  const avatarSrc = baseSrc ? `${baseSrc}${cacheBust ? `?t=${cacheBust}` : ""}` : undefined;
  const initials = name ? toInitials(name) : undefined;

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setError(null);
    setUploading(true);
    try {
      const newUrl = await onUpload(file);
      if (newUrl) {
        setLocalSrc(newUrl);
        setCacheBust(Date.now());
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to upload photo");
    } finally {
      setUploading(false);
    }
  }

  return (
    <Stack alignItems="center" gap={0.5}>
      <CircularButton
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        aria-label={label}
      >
        <SizedAvatar src={avatarSrc} $size={size}>
          {!baseSrc && initials}
        </SizedAvatar>
        {uploading && (
          <UploadOverlay>
            <WhiteSpinner size={size * 0.35} />
          </UploadOverlay>
        )}
      </CircularButton>
      <Typography variant="caption" color={error ? "error" : "text.secondary"}>
        {error ?? (uploading ? "Uploading…" : label)}
      </Typography>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        hidden
        onChange={(e) => void handleFileChange(e)}
      />
    </Stack>
  );
}
